import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart3,
  Download,
  ChevronDown,
  ChevronUp,
  RefreshCcw,
  FileSpreadsheet,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { isSaudiBrand } from '../../../products/saudi/utils/isSaudiBrand';
import QawaemStatsCard from '../../../products/saudi/components/QawaemStatsCard';
import AnalyticsChartCard from './AnalyticsChartCard';
import { exportToCSV } from '../../utils/importExportUtils';
import { exportToPDF } from '../../utils/exportUtils';

const KPI_VARIANTS = ['copper', 'forest', 'copper', 'forest'];
const MAX_KPI_CARDS = 4;

/**
 * Module analytics report shell — flat KPI strip, chart grid, export table.
 * Date filtering uses the global header bar (SaudiDateFilterContext), not inline presets.
 */
export default function ModuleAnalyticsSummaryPage({
  title,
  subtitle,
  loading = false,
  onRefresh,
  stats = [],
  insights = [],
  charts = [],
  table = null,
  exportFilePrefix = 'analytics',
  emptyLabel,
}) {
  const { t } = useTranslation();
  const qawaem = isSaudiBrand();
  const exportRef = useRef(null);
  const [showExport, setShowExport] = useState(false);
  const empty = emptyLabel || t('common.no_data');

  useEffect(() => {
    const handler = (e) => {
      if (exportRef.current && !exportRef.current.contains(e.target)) setShowExport(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const kpiCards = stats.slice(0, MAX_KPI_CARDS);
  const summaryRows = kpiCards.map((s) => ({ metric: s.label, value: String(s.value ?? '—') }));
  const tableRows = table?.rows?.length ? table.rows : summaryRows;
  const tableColumns = table?.columns?.length
    ? table.columns
    : [
        { key: 'metric', label: t('common.indicator', { defaultValue: 'المؤشر' }) },
        { key: 'value', label: t('common.value', { defaultValue: 'القيمة' }) },
      ];

  const chartGridClass =
    charts.length >= 3
      ? 'qawaem-analytics-charts-grid qawaem-analytics-charts-grid--3'
      : charts.length > 1
        ? 'qawaem-analytics-charts-grid qawaem-analytics-charts-grid--2'
        : 'qawaem-analytics-charts-grid';

  const buildPdfRows = () =>
    tableRows.map((row) => {
      const out = {};
      tableColumns.forEach((col) => {
        out[col.label] = row[col.key] ?? '—';
      });
      return out;
    });

  const handleExportCSV = () => {
    if (!tableRows.length) {
      toast.error(empty);
      return;
    }
    exportToCSV(tableRows, tableColumns, `${exportFilePrefix}_${new Date().toISOString().slice(0, 10)}.csv`);
    toast.success(t('common.export_success', { defaultValue: 'تم التصدير' }));
    setShowExport(false);
  };

  const handleExportPDF = async () => {
    if (!tableRows.length) {
      toast.error(empty);
      return;
    }
    const ok = await exportToPDF(
      buildPdfRows(),
      `${exportFilePrefix}_${new Date().toISOString().slice(0, 10)}`,
      title,
    );
    if (ok) toast.success(t('common.export_success', { defaultValue: 'تم التصدير' }));
    else toast.info(t('common.coming_soon', { defaultValue: 'قريباً' }));
    setShowExport(false);
  };

  return (
    <div className={`space-y-6 employees-container ${qawaem ? 'qawaem-analytics-page' : ''}`}>
      <div className="flex justify-between items-start list-toolbar-mobile-layout gap-4">
        <div className="min-w-0">
          <h1
            className={`text-3xl font-bold flex items-center gap-3 flex-wrap ${
              qawaem ? 'text-forest-900' : 'text-white'
            }`}
          >
            {qawaem ? (
              <BarChart3 className="page-title-icon page-title-icon--bronze h-8 w-8 shrink-0" />
            ) : (
              <div className="p-3 rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-800">
                <BarChart3 className="h-8 w-8 text-white" />
              </div>
            )}
            {title}
            {onRefresh ? (
              <button type="button" onClick={onRefresh} className="refresh-button" title={t('common.refresh')}>
                <RefreshCcw size={20} className={loading ? 'animate-spin' : ''} />
              </button>
            ) : null}
          </h1>
          {subtitle ? (
            <p className={qawaem ? 'page-section-desc' : 'text-white/60 mt-2'}>{subtitle}</p>
          ) : null}
          {qawaem ? (
            <div className="qawaem-analytics-header-meta">
              <span className="qawaem-analytics-pill">
                <FileSpreadsheet className="h-3.5 w-3.5" aria-hidden />
                {t('common.analytics_report', { defaultValue: 'تقرير تحليلي' })}
              </span>
              {tableRows.length ? (
                <span className="text-xs text-console-muted">
                  {t('common.rows_count', { defaultValue: '{{count}} صف', count: tableRows.length })}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="relative shrink-0" ref={exportRef}>
          <button
            type="button"
            onClick={() => setShowExport((v) => !v)}
            className={
              qawaem
                ? 'btn-stat-green btn-header-compact'
                : 'bg-white/5 border border-white/20 hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold transition-all flex items-center gap-2'
            }
          >
            <Download className="h-5 w-5" />
            <span className="hidden sm:inline">{t('common.export')}</span>
            {showExport ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          {showExport ? (
            <div
              className={
                qawaem
                  ? 'absolute end-0 mt-2 w-44 qawaem-dash-panel !p-1 z-20 shadow-lg'
                  : 'absolute end-0 mt-2 w-44 bg-slate-900 border border-white/20 rounded-xl shadow-xl z-20 overflow-hidden'
              }
            >
              <button
                type="button"
                onClick={handleExportPDF}
                className={qawaem ? 'qawaem-export-menu-item' : 'w-full px-4 py-2 text-start text-white/80 hover:bg-white/10'}
              >
                PDF
              </button>
              <button
                type="button"
                onClick={handleExportCSV}
                className={qawaem ? 'qawaem-export-menu-item' : 'w-full px-4 py-2 text-start text-white/80 hover:bg-white/10'}
              >
                CSV
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {insights.length > 0 ? (
        <div className={qawaem ? 'qawaem-analytics-insights' : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3'}>
          {insights.slice(0, 4).map((item) => (
            <div key={item.label} className={qawaem ? 'qawaem-analytics-insight' : 'bg-white/10 rounded-xl p-4 border border-white/20'}>
              <p className={qawaem ? 'qawaem-analytics-insight-label' : 'text-white/60 text-xs'}>{item.label}</p>
              <p className={qawaem ? 'qawaem-analytics-insight-value' : 'text-xl font-bold text-white mt-1'}>
                {item.value}
              </p>
              {item.hint ? (
                <p className={qawaem ? 'qawaem-analytics-insight-hint' : 'text-white/40 text-xs mt-0.5'}>{item.hint}</p>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      <div className={qawaem ? 'qawaem-analytics-kpi-strip' : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'}>
        {kpiCards.map((stat, i) =>
          qawaem ? (
            <QawaemStatsCard
              key={stat.label}
              label={stat.label}
              value={stat.value}
              icon={stat.icon || BarChart3}
              variant={stat.variant || KPI_VARIANTS[i]}
              delay={i * 0.06}
            />
          ) : (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
              className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
            >
              <p className="text-white/60 text-sm">{stat.label}</p>
              <p className="text-2xl font-bold text-white mt-2">{stat.value}</p>
            </motion.div>
          )
        )}
      </div>

      {charts.length > 0 ? (
        <div>
          <div className={qawaem ? 'qawaem-analytics-section-head mb-3' : 'mb-3'}>
            <h2 className={qawaem ? 'qawaem-analytics-section-title' : 'text-lg font-semibold text-white'}>
              {t('common.visual_analytics', { defaultValue: 'تحليلات مرئية' })}
            </h2>
            <span className={qawaem ? 'qawaem-analytics-section-sub' : 'text-white/50 text-sm'}>
              {t('common.charts_count', { defaultValue: '{{count}} مخطط', count: charts.length })}
            </span>
          </div>
          <div className={qawaem ? chartGridClass : `grid gap-4 ${charts.length > 1 ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`}>
            {charts.map((chart) => (
              <AnalyticsChartCard key={chart.title} chart={chart} empty={empty} qawaem={qawaem} />
            ))}
          </div>
        </div>
      ) : null}

      <div className={qawaem ? 'qawaem-dash-panel !p-0 overflow-hidden' : 'bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden'}>
        <div className={`p-4 ${qawaem ? 'border-b border-[#6e9b6e]/12' : 'border-b border-white/10'}`}>
          <div className={qawaem ? 'qawaem-analytics-section-head !mb-0' : ''}>
            <h2 className={`text-lg font-semibold ${qawaem ? 'text-base text-forest-900 qawaem-analytics-section-title' : 'text-white'}`}>
              {table?.title || t('common.detailed_report', { defaultValue: 'تقرير تفصيلي' })}
            </h2>
            {tableRows.length ? (
              <span className={qawaem ? 'qawaem-analytics-section-sub' : 'text-white/50 text-sm'}>
                {t('common.exportable', { defaultValue: 'قابل للتصدير' })}
              </span>
            ) : null}
          </div>
        </div>
        {loading ? (
          <div className={`p-8 text-center ${qawaem ? 'text-console-muted' : 'text-white/50'}`}>
            {t('common.loading')}
          </div>
        ) : (
          <div className={`overflow-x-auto ${qawaem ? 'glass-table-scroll' : ''}`}>
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  {tableColumns.map((col) => (
                    <th key={col.key} className="px-4 py-3 text-start text-sm font-semibold">
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row, idx) => (
                  <tr
                    key={idx}
                    className={`glass-table-row ${qawaem ? 'border-b border-[#6e9b6e]/8' : 'border-b border-white/5'}`}
                  >
                    {tableColumns.map((col) => (
                      <td
                        key={col.key}
                        className={`px-4 py-3 text-sm ${qawaem ? 'text-forest-900' : ''}`}
                      >
                        {row[col.key] ?? '—'}
                      </td>
                    ))}
                  </tr>
                ))}
                {!tableRows.length ? (
                  <tr>
                    <td
                      colSpan={tableColumns.length}
                      className={`px-4 py-10 text-center ${qawaem ? 'text-console-muted' : 'text-white/50'}`}
                    >
                      {empty}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
